import React, { useState, useRef } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Award, Plus, Trash2, CheckCircle2, Sparkles, Pencil, X, MinusCircle, HelpCircle } from 'lucide-react'

export interface MarkingRulePayload {
  id?: number
  name: string
  question_type: string
  scoring_strategy: string
  scoring_params: Record<string, any>
  marked_for_review_rule?: string
  description?: string
}

interface Props {
  rules: MarkingRulePayload[]
  onSaveRule: (rule: MarkingRulePayload) => void
  onDeleteRule?: (id: number) => void
}

const PRESET_TEMPLATES = [
  {
    title: 'Standard MCQ (+4 / -1)',
    question_type: 'MCQ',
    scoring_strategy: 'standard',
    scoring_params: { correct: 4.0, wrong: -1.0, unattempted: 0.0 },
    marked_for_review_rule: 'counts_if_answered',
    description: 'Standard engineering / medical MCQ pattern with 1 mark penalty.'
  },
  {
    title: 'Multi-Correct Partial (JEE Advanced)',
    question_type: 'MULTI_CORRECT',
    scoring_strategy: 'partial',
    scoring_params: { full_correct: 4.0, per_correct_option: 1.0, any_wrong: -2.0, unattempted: 0.0 },
    marked_for_review_rule: 'counts_if_answered',
    description: 'JEE Advanced style: full mark for all correct, +1 per correct option with no wrong, -2 penalty.'
  },
  {
    title: 'Numerical / NAT (No Penalty)',
    question_type: 'NUMERICAL',
    scoring_strategy: 'no_penalty',
    scoring_params: { correct: 3.0, wrong: 0.0, unattempted: 0.0 },
    marked_for_review_rule: 'counts_if_answered',
    description: 'Integer response type without negative penalty.'
  },
  {
    title: 'Banking Standard (+1 / -0.25)',
    question_type: 'MCQ',
    scoring_strategy: 'standard',
    scoring_params: { correct: 1.0, wrong: -0.25, unattempted: 0.0 },
    marked_for_review_rule: 'counts_if_answered',
    description: 'Standard banking & insurance sectional drill.'
  },
  {
    title: 'Civil Services / UPSC (1/3rd Penalty)',
    question_type: 'MCQ',
    scoring_strategy: 'custom',
    scoring_params: { correct: 2.0, wrong: -0.66, unattempted: 0.0 },
    marked_for_review_rule: 'counts_if_answered',
    description: 'UPSC Prelims GS 2-mark paper with 1/3rd negative marking.'
  },
  {
    title: 'XAT Style (Unattempted Penalty)',
    question_type: 'MCQ',
    scoring_strategy: 'custom',
    scoring_params: { correct: 1.0, wrong: -0.25, unattempted: -0.10 },
    marked_for_review_rule: 'counts_if_answered',
    description: 'XAT pattern: 1 mark for correct, -0.25 for incorrect, and -0.10 penalty for unattempted.'
  }
]

export function MarkingRulesManager({ rules, onSaveRule, onDeleteRule }: Props) {
  const formRef = useRef<HTMLDivElement>(null)
  const defaultRule: MarkingRulePayload = {
    name: '',
    question_type: 'MCQ',
    scoring_strategy: 'standard',
    scoring_params: { correct: 1.0, wrong: 0.0, unattempted: 0.0 },
    marked_for_review_rule: 'counts_if_answered',
    description: ''
  }

  const [editingRule, setEditingRule] = useState<MarkingRulePayload>(defaultRule)
  const [negativeEnabled, setNegativeEnabled] = useState(false)
  const [unattemptedPenaltyEnabled, setUnattemptedPenaltyEnabled] = useState(false)

  const applyTemplate = (tpl: typeof PRESET_TEMPLATES[0]) => {
    const hasPenalty = Boolean(
      (tpl.scoring_params.wrong != null && Number(tpl.scoring_params.wrong) !== 0) ||
      (tpl.scoring_params.any_wrong != null && Number(tpl.scoring_params.any_wrong) !== 0)
    )
    setNegativeEnabled(hasPenalty)
    const hasUnattemptedPenalty = Boolean(
      tpl.scoring_params.unattempted != null && Number(tpl.scoring_params.unattempted) !== 0
    )
    setUnattemptedPenaltyEnabled(hasUnattemptedPenalty)
    setEditingRule({
      name: tpl.title,
      question_type: tpl.question_type,
      scoring_strategy: tpl.scoring_strategy,
      scoring_params: { ...tpl.scoring_params },
      marked_for_review_rule: tpl.marked_for_review_rule || 'counts_if_answered',
      description: tpl.description
    })
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleEditRule = (r: MarkingRulePayload) => {
    const params = r.scoring_params || {}
    const hasPenalty = Boolean(
      (params.wrong != null && Number(params.wrong) !== 0) ||
      (params.any_wrong != null && Number(params.any_wrong) !== 0)
    )
    setNegativeEnabled(hasPenalty)
    const hasUnattemptedPenalty = Boolean(
      params.unattempted != null && Number(params.unattempted) !== 0
    )
    setUnattemptedPenaltyEnabled(hasUnattemptedPenalty)
    setEditingRule({
      id: r.id,
      name: r.name,
      question_type: r.question_type,
      scoring_strategy: r.scoring_strategy,
      scoring_params: { ...params },
      marked_for_review_rule: r.marked_for_review_rule || 'counts_if_answered',
      description: r.description || ''
    })
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleCancelEdit = () => {
    setNegativeEnabled(false)
    setUnattemptedPenaltyEnabled(false)
    setEditingRule(defaultRule)
  }

  const handleToggleNegative = (checked: boolean) => {
    setNegativeEnabled(checked)
    setEditingRule(prev => {
      const prevWrong = prev.scoring_params.wrong
      const prevAnyWrong = prev.scoring_params.any_wrong
      return {
        ...prev,
        scoring_params: {
          ...prev.scoring_params,
          wrong: checked ? (prevWrong && Number(prevWrong) !== 0 ? prevWrong : -0.25) : 0.0,
          any_wrong: checked ? (prevAnyWrong && Number(prevAnyWrong) !== 0 ? prevAnyWrong : -1.0) : 0.0
        }
      }
    })
  }

  const handleToggleUnattemptedPenalty = (checked: boolean) => {
    setUnattemptedPenaltyEnabled(checked)
    setEditingRule(prev => {
      const prevUnattempted = prev.scoring_params.unattempted
      return {
        ...prev,
        scoring_params: {
          ...prev.scoring_params,
          unattempted: checked
            ? (prevUnattempted && Number(prevUnattempted) !== 0 ? prevUnattempted : -0.10)
            : 0.0
        }
      }
    })
  }

  const handleParamChange = (key: string, val: string) => {
    setEditingRule(prev => ({
      ...prev,
      scoring_params: {
        ...prev.scoring_params,
        [key]: val === '' ? '' : (isNaN(parseFloat(val)) ? val : parseFloat(val))
      }
    }))
  }

  const handleSave = () => {
    const p = editingRule.scoring_params || {}
    const parsedCorrect = parseFloat(String(p.correct))
    const parsedWrong = parseFloat(String(p.wrong))
    const parsedUnattempted = parseFloat(String(p.unattempted))
    const finalRule: MarkingRulePayload = {
      ...editingRule,
      scoring_params: {
        ...p,
        correct: isNaN(parsedCorrect) ? 1.0 : parsedCorrect,
        wrong: negativeEnabled ? -Math.abs(isNaN(parsedWrong) ? 0.25 : parsedWrong) : 0.0,
        unattempted: unattemptedPenaltyEnabled ? -Math.abs(isNaN(parsedUnattempted) ? 0.10 : parsedUnattempted) : 0.0
      }
    }
    if (editingRule.scoring_strategy === 'partial') {
      const parsedFull = parseFloat(String(p.full_correct))
      const parsedPerOpt = parseFloat(String(p.per_correct_option))
      const parsedAnyWrong = parseFloat(String(p.any_wrong))
      finalRule.scoring_params.full_correct = isNaN(parsedFull) ? 4.0 : parsedFull
      finalRule.scoring_params.per_correct_option = isNaN(parsedPerOpt) ? 1.0 : parsedPerOpt
      finalRule.scoring_params.any_wrong = negativeEnabled ? -Math.abs(isNaN(parsedAnyWrong) ? 1.0 : parsedAnyWrong) : 0.0
    }
    onSaveRule(finalRule)
    handleCancelEdit()
  }

  return (
    <div className="space-y-6">
      {/* 1. Preset Examples Inspiration Strip */}
      <div className="space-y-2">
        <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-primary" /> Start with a Predefined Example or Template
        </Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PRESET_TEMPLATES.map((tpl, i) => (
            <div
              key={i}
              onClick={() => applyTemplate(tpl)}
              className="p-3 bg-card border border-border/80 hover:border-primary/50 hover:bg-primary/5 rounded-xl cursor-pointer transition-all space-y-1.5 shadow-2xs"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-foreground">{tpl.title}</span>
                <Badge variant="outline" className="text-[9px] font-mono">{tpl.question_type}</Badge>
              </div>
              <p className="text-[11px] text-muted-foreground line-clamp-2">{tpl.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 2. Custom Rule Builder Form */}
      <Card ref={formRef} className="border-border bg-card shadow-glass-xs rounded-2xl scroll-mt-6">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between border-b border-border/60 pb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-wider text-foreground flex items-center gap-2">
                <Award className="h-4 w-4 text-primary" />
                {editingRule.id ? (
                  <>
                    Edit Marking Rule: <span className="text-primary font-extrabold">{editingRule.name}</span>
                  </>
                ) : (
                  'Configure Custom Marking Rule'
                )}
              </span>
              {editingRule.id && (
                <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30 uppercase font-bold">
                  Editing
                </Badge>
              )}
            </div>

            {editingRule.id && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancelEdit}
                className="text-xs h-7 text-muted-foreground hover:text-foreground gap-1 rounded-lg"
              >
                <X className="h-3.5 w-3.5" /> Cancel Edit
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Rule Name</Label>
              <Input
                value={editingRule.name}
                onChange={e => setEditingRule(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Section A Physics MCQ"
                className="h-9 text-xs"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Question Type</Label>
              <Select
                value={editingRule.question_type}
                onValueChange={(val) => setEditingRule(p => ({ ...p, question_type: val }))}
              >
                <SelectTrigger className="w-full h-9 rounded-lg border border-border bg-card text-xs font-medium focus:ring-primary">
                  <SelectValue placeholder="Select Question Type" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-border bg-popover text-foreground shadow-glass-md z-50">
                  <SelectGroup>
                    <SelectItem value="MCQ" className="text-xs font-medium cursor-pointer">MCQ (Single Correct)</SelectItem>
                    <SelectItem value="MULTI_CORRECT" className="text-xs font-medium cursor-pointer">Multi-Correct (MSQ)</SelectItem>
                    <SelectItem value="NUMERICAL" className="text-xs font-medium cursor-pointer">Numerical / Integer</SelectItem>
                    <SelectItem value="ASSERTION_REASON" className="text-xs font-medium cursor-pointer">Assertion Reason</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Scoring Strategy</Label>
              <Select
                value={editingRule.scoring_strategy}
                onValueChange={(val) => setEditingRule(p => ({ ...p, scoring_strategy: val }))}
              >
                <SelectTrigger className="w-full h-9 rounded-lg border border-border bg-card text-xs font-medium focus:ring-primary">
                  <SelectValue placeholder="Select Scoring Strategy" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-border bg-popover text-foreground shadow-glass-md z-50">
                  <SelectGroup>
                    <SelectItem value="standard" className="text-xs font-medium cursor-pointer">Standard (Pos / Neg)</SelectItem>
                    <SelectItem value="partial" className="text-xs font-medium cursor-pointer">Partial Credit (Multi-Correct)</SelectItem>
                    <SelectItem value="no_penalty" className="text-xs font-medium cursor-pointer">No Penalty (Non-negative)</SelectItem>
                    <SelectItem value="custom" className="text-xs font-medium cursor-pointer">Custom Parameters</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Marked for Review Rule</Label>
              <Select
                value={editingRule.marked_for_review_rule || 'counts_if_answered'}
                onValueChange={(val) => setEditingRule(p => ({ ...p, marked_for_review_rule: val }))}
              >
                <SelectTrigger className="w-full h-9 rounded-lg border border-border bg-card text-xs font-medium focus:ring-primary">
                  <SelectValue placeholder="Select MFR Rule" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-border bg-popover text-foreground shadow-glass-md z-50">
                  <SelectGroup>
                    <SelectItem value="counts_if_answered" className="text-xs font-medium cursor-pointer">Counts if Answered (NTA)</SelectItem>
                    <SelectItem value="never_counts" className="text-xs font-medium cursor-pointer">Never Counts (GATE)</SelectItem>
                    <SelectItem value="always_counts" className="text-xs font-medium cursor-pointer">Always Counts</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Dynamic Params Controls */}
          <div className="p-4 bg-muted/30 border border-border/70 rounded-xl space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-start">
              {/* Correct Mark */}
              <div className="space-y-1.5">
                <Label className="text-[10px] font-bold uppercase text-primary">Correct Mark (+)</Label>
                <Input
                  type="number"
                  step="any"
                  min="0"
                  placeholder="e.g. 1"
                  value={editingRule.scoring_params.correct ?? ''}
                  onChange={e => handleParamChange('correct', e.target.value)}
                  className="h-9 text-xs font-bold bg-card"
                />
              </div>

              {/* Negative Marking Switch & Penalty */}
              <div className="space-y-2 p-3 rounded-xl bg-card border border-border/70">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="negative-toggle" className="text-[10px] font-bold uppercase text-destructive flex items-center gap-1.5 cursor-pointer">
                    <MinusCircle className="h-3.5 w-3.5" /> Negative Marking
                  </Label>
                  <Switch
                    id="negative-toggle"
                    checked={negativeEnabled}
                    onCheckedChange={handleToggleNegative}
                  />
                </div>

                {negativeEnabled ? (
                  <div className="space-y-1 pt-1.5 border-t border-border/50">
                    <Label className="text-[10px] font-semibold text-destructive flex items-center justify-between">
                      <span>Incorrect Penalty</span>
                      <span className="font-mono text-[9px]">(-)</span>
                    </Label>
                    <Input
                      type="number"
                      step="any"
                      min="0"
                      placeholder="e.g. 0.25 or 1"
                      value={
                        editingRule.scoring_params.wrong !== undefined && editingRule.scoring_params.wrong !== ''
                          ? Math.abs(Number(editingRule.scoring_params.wrong)) || ''
                          : ''
                      }
                      onChange={e => {
                        const val = e.target.value
                        handleParamChange('wrong', val === '' ? '' : String(-Math.abs(parseFloat(val) || 0)))
                      }}
                      className="h-8 text-xs font-bold text-destructive bg-background"
                    />
                  </div>
                ) : (
                  <div className="text-[11px] text-muted-foreground pt-0.5 flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                    <span>No negative penalty (0)</span>
                  </div>
                )}
              </div>

              {/* Unattempted Switch & Penalty */}
              <div className="space-y-2 p-3 rounded-xl bg-card border border-border/70">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="unattempted-toggle" className="text-[10px] font-bold uppercase text-muted-foreground flex items-center gap-1.5 cursor-pointer">
                    <HelpCircle className="h-3.5 w-3.5" /> Unattempted Penalty
                  </Label>
                  <Switch
                    id="unattempted-toggle"
                    checked={unattemptedPenaltyEnabled}
                    onCheckedChange={handleToggleUnattemptedPenalty}
                  />
                </div>

                {unattemptedPenaltyEnabled ? (
                  <div className="space-y-1 pt-1.5 border-t border-border/50">
                    <Label className="text-[10px] font-semibold text-destructive flex items-center justify-between">
                      <span>Penalty per Unattempted</span>
                      <span className="font-mono text-[9px]">(-)</span>
                    </Label>
                    <Input
                      type="number"
                      step="any"
                      min="0"
                      placeholder="e.g. 0.10 or 0.25"
                      value={
                        editingRule.scoring_params.unattempted !== undefined && editingRule.scoring_params.unattempted !== ''
                          ? Math.abs(Number(editingRule.scoring_params.unattempted)) || ''
                          : ''
                      }
                      onChange={e => {
                        const val = e.target.value
                        handleParamChange('unattempted', val === '' ? '' : String(-Math.abs(parseFloat(val) || 0)))
                      }}
                      className="h-8 text-xs font-bold text-destructive bg-background"
                    />
                  </div>
                ) : (
                  <div className="text-[11px] text-muted-foreground pt-0.5 flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                    <span>No penalty for skipping (0)</span>
                  </div>
                )}
              </div>
            </div>

            {/* Special Multi-Correct Partial Inputs if strategy === 'partial' */}
            {editingRule.scoring_strategy === 'partial' && (
              <div className="pt-3 border-t border-border/60 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase text-primary">Full Correct (+)</Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="e.g. 4"
                    value={editingRule.scoring_params.full_correct ?? ''}
                    onChange={e => handleParamChange('full_correct', e.target.value)}
                    className="h-8 text-xs font-bold"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase text-secondary">Per Correct Option (+)</Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="e.g. 1"
                    value={editingRule.scoring_params.per_correct_option ?? ''}
                    onChange={e => handleParamChange('per_correct_option', e.target.value)}
                    className="h-8 text-xs font-bold"
                  />
                </div>
                {negativeEnabled && (
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase text-destructive">Any Wrong Selected (-)</Label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="e.g. 2"
                      value={
                        editingRule.scoring_params.any_wrong !== undefined && editingRule.scoring_params.any_wrong !== ''
                          ? Math.abs(Number(editingRule.scoring_params.any_wrong)) || ''
                          : ''
                      }
                      onChange={e => {
                        const val = e.target.value
                        handleParamChange('any_wrong', val === '' ? '' : String(-Math.abs(parseFloat(val) || 0)))
                      }}
                      className="h-8 text-xs font-bold text-destructive"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            {editingRule.id && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCancelEdit}
                className="text-xs rounded-xl h-9"
              >
                Cancel
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              disabled={!editingRule.name.trim()}
              onClick={handleSave}
              className="gap-1.5 text-xs font-bold rounded-xl h-9 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> {editingRule.id ? 'Update Rule' : 'Save Rule'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 3. Existing Rules List */}
      {rules && rules.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Configured Marking Rules ({rules.length})
          </Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {rules.map((r, idx) => {
              const isSelected = Boolean(editingRule.id && editingRule.id === r.id)
              return (
                <div
                  key={r.id || idx}
                  className={`p-3.5 bg-card border rounded-xl flex items-center justify-between gap-3 shadow-2xs transition-all ${
                    isSelected
                      ? 'border-primary ring-1 ring-primary/40 bg-primary/5'
                      : 'border-border/80 hover:border-primary/40'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-foreground truncate">{r.name}</span>
                      {isSelected && (
                        <Badge variant="outline" className="text-[9px] font-bold uppercase bg-primary text-primary-foreground px-1.5 py-0">
                          Editing
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mt-0.5 flex-wrap">
                      <Badge variant="outline" className="px-1 py-0">{r.question_type}</Badge>
                      <Badge variant="outline" className="px-1 py-0 bg-primary/10 text-primary border-primary/20">{r.scoring_strategy}</Badge>
                      {r.marked_for_review_rule && (
                        <Badge variant="secondary" className="px-1 py-0 text-[9px]">
                          MFR: {r.marked_for_review_rule.replace(/_/g, ' ')}
                        </Badge>
                      )}
                      {r.scoring_params?.unattempted != null && Number(r.scoring_params.unattempted) !== 0 && (
                        <Badge variant="outline" className="px-1 py-0 text-[9px] text-destructive border-destructive/30 bg-destructive/5">
                          Unattempted: {r.scoring_params.unattempted}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground rounded-lg"
                      title="Edit this marking rule"
                      onClick={() => handleEditRule(r)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {onDeleteRule && r.id && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:bg-destructive/10 rounded-lg"
                        title="Delete this marking rule"
                        onClick={() => onDeleteRule(r.id!)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
